import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaBars, FaBookOpen, FaBrain, FaUsers, FaVolumeUp, FaPlayCircle, FaCheck, FaChalkboardTeacher, FaUserTie, FaLightbulb, FaHandsHelping, FaQuestion, FaComment, FaEye, FaInstagram, FaYoutube, FaTelegram, FaTwitter, FaChevronRight, FaGlobe, FaStar, FaHeadphones, FaBook } from 'react-icons/fa';
import './Home.css'; // For custom styles like .persian-font, .gradient-bg, etc.
import { useAuth } from '../contexts/AuthContext';
import { getWordOfTheDayAudioUrl } from '../utils/audioUrlMapper';
import { API_BASE_URL } from '../config/api';
import SEO from '../components/SEO';

interface WordOfTheDay {
  persian: string;
  english: string;
  transliteration: string;
  category: string;
  difficulty: string;
  example: string;
  audioUrl?: string;
}

const Home: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [wordOfTheDay, setWordOfTheDay] = useState<WordOfTheDay>({
    persian: 'خوش آمدید',
    english: 'Welcome',
    transliteration: 'Khosh amadid',
    category: 'Greetings',
    difficulty: 'beginner',
    example: 'خوش آمدید! چطور می‌تونم کمکتون کنم؟',
    audioUrl: '/audio/khosh-amadid.mp3'
  });
  const [loadingWord, setLoadingWord] = useState(true);
  const { user } = useAuth();

  // Play audio for pronunciation
  const playAudio = async (url?: string) => {
    // Get the proper audio URL for word of the day
    const audioUrl = url || getWordOfTheDayAudioUrl(wordOfTheDay.persian, wordOfTheDay.english);
    console.log('🎵 Using Home page audio URL:', audioUrl);
    
    if (!audioUrl) {
      console.warn('No audio URL provided');
      // Try to use browser's speech synthesis as fallback
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(wordOfTheDay.persian);
        utterance.lang = 'fa-IR';
        utterance.rate = 0.8;
        speechSynthesis.speak(utterance);
      }
      return;
    }
    
    try {
      
      if (audioUrl.includes('/audio/unknown-pathway/unknown-lesson/audio.mp3') && !audioUrl.includes('http')) {
        console.log('🎤 Generating ganji-adabi audio for:', wordOfTheDay.persian);
        
        try {
          
          const response = await fetch(`${API_BASE_URL}/audio/unknown-pathway/unknown-lesson/audio.mp3`);
          
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.audioUrl) {
              console.log('✅ ganji-adabi audio generated successfully:', data.audioUrl);
              // Use the generated audio URL
              const finalAudioUrl = data.audioUrl;
              const audio = new Audio(finalAudioUrl);
              await audio.play();
              return;
            } else {
              console.log('⚠️ ganji-adabi generation failed, will use TTS fallback');
              // Fall back to speech synthesis
              if ('speechSynthesis' in window) {
                const utterance = new SpeechSynthesisUtterance(wordOfTheDay.persian);
                utterance.lang = 'fa-IR';
                utterance.rate = 0.8;
                speechSynthesis.speak(utterance);
                return;
              }
            }
          } else {
            console.log('⚠️ ganji-adabi server error, will use TTS fallback');
            // Fall back to speech synthesis
            if ('speechSynthesis' in window) {
              const utterance = new SpeechSynthesisUtterance(wordOfTheDay.persian);
              utterance.lang = 'fa-IR';
              utterance.rate = 0.8;
              speechSynthesis.speak(utterance);
              return;
            }
          }
        } catch (error) {
          console.error('❌ ganji-adabi generation failed:', error);
          // Fall back to speech synthesis
          if ('speechSynthesis' in window) {
            const utterance = new SpeechSynthesisUtterance(wordOfTheDay.persian);
            utterance.lang = 'fa-IR';
            utterance.rate = 0.8;
            speechSynthesis.speak(utterance);
            return;
          }
        }
      } else {
        // For direct audio URLs, play them directly
        const audio = new Audio(audioUrl);
        await audio.play();
      }


      
    } catch (err: any) {
      console.error('Audio error:', err);
      // Try to use browser's speech synthesis as fallback
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(wordOfTheDay.persian);
        utterance.lang = 'fa-IR';
        utterance.rate = 0.8;
        speechSynthesis.speak(utterance);
        console.log('🎤 Using speech synthesis fallback for:', wordOfTheDay.persian);
      } else {
        console.warn('Audio playback failed and speech synthesis not available');
      }
    }
  };

  // Fetch word of the day
  useEffect(() => {
    const fetchWordOfTheDay = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/words/day`);
        if (response.ok) {
          const responseData = await response.json();
          if (responseData.success && responseData.data) {
            setWordOfTheDay(responseData.data);
          }
        }
      } catch (error) {
        console.error('Error fetching word of the day:', error);
        // Keep the default word if API fails
      } finally {
        setLoadingWord(false);
      }
    };

    fetchWordOfTheDay();
  }, []);

  // Dummy testimonials data for carousel
  const testimonials = [
    {
      img: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80',
      name: 'Sarah Johnson',
      role: 'Beginner learner from USA',
      text: '"I\'ve tried several Farsi learning apps, but Pirooz stands out because of its cultural insights and community. The lessons feel personal, not robotic. I can now have basic conversations with my Persian friends!"',
    },
    {
      img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80',
      name: 'David Chen',
      role: 'Intermediate learner from Canada',
      text: '"The Pirooz Plus subscription is worth every penny. The offline access lets me study during my commute, and the live Q&A sessions have helped me understand tricky grammar points. مرسی Vajak!"',
    },
    {
      img: 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80',
      name: 'Sanaz Khorsandi',
      role: 'Persian teacher from Iran',
      text: '"As a teacher, I appreciate how Pirooz makes it easy to create interactive lessons. The platform handles payments and student management, so I can focus on teaching. My students love the community features too."',
    },
    {
      img: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80',
      name: 'Michael Brown',
      role: 'Advanced learner from UK',
      text: '"After 2 years with Pirooz, I can confidently say it\'s the best resource for learning Persian. The cultural context in lessons is invaluable, and the forum has connected me with native speakers for practice."',
    },
  ];

  // Sidebar component
  const Sidebar = () => (
    <div className={`fixed top-0 right-0 h-full w-80 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out z-50 ${sidebarOpen ? 'translate-x-0' : 'translate-x-full'}`}>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <span className="text-2xl font-bold text-blue-600 persian-font">پیروز</span>
            <span className="ml-2 text-xl font-bold text-gray-800">Pirooz</span>
          </div>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            <FaBars className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Learn Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <FaBookOpen className="mr-2 text-blue-600" />
              📚 Learn
            </h3>
            <div className="space-y-3">
              <Link to="/alphabet-learning" className="flex items-center p-3 rounded-lg hover:bg-blue-50 transition-colors">
                <span className="mr-3">✏️</span>
                <span className="text-gray-700">Alphabet & Writing</span>
              </Link>
              <Link to="/vocabulary" className="flex items-center p-3 rounded-lg hover:bg-blue-50 transition-colors">
                <span className="mr-3">📖</span>
                <span className="text-gray-700">Vocabulary Builder</span>
              </Link>
              <Link to="/idioms" className="flex items-center p-3 rounded-lg hover:bg-blue-50 transition-colors">
                <span className="mr-3">🐦</span>
                <span className="text-gray-700">Idioms & Expressions</span>
              </Link>
              <Link to="/pathways" className="flex items-center p-3 rounded-lg hover:bg-blue-50 transition-colors">
                <span className="mr-3">🧩</span>
                <span className="text-gray-700">Beginner to Advanced Courses</span>
              </Link>
            </div>
          </div>

          {/* Practice Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <FaBrain className="mr-2 text-green-600" />
              🧠 Practice
            </h3>
            <div className="space-y-3">
              <Link to="/quiz" className="flex items-center p-3 rounded-lg hover:bg-green-50 transition-colors">
                <span className="mr-3">🎯</span>
                <span className="text-gray-700">Quizzes</span>
              </Link>
              <Link to="/progress" className="flex items-center p-3 rounded-lg hover:bg-green-50 transition-colors">
                <span className="mr-3">📈</span>
                <span className="text-gray-700">Progress Tracker</span>
              </Link>
              <Link to="/flashcards" className="flex items-center p-3 rounded-lg hover:bg-green-50 transition-colors">
                <span className="mr-3">🃏</span>
                <span className="text-gray-700">Flashcards</span>
              </Link>
            </div>
          </div>

          {/* Community Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <FaUsers className="mr-2 text-orange-600" />
              👥 Community
            </h3>
            <div className="space-y-3">
              <Link to="/forum" className="flex items-center p-3 rounded-lg hover:bg-orange-50 transition-colors">
                <span className="mr-3">💬</span>
                <span className="text-gray-700">Forum</span>
              </Link>
              <Link to="/teacher-qa" className="flex items-center p-3 rounded-lg hover:bg-orange-50 transition-colors">
                <span className="mr-3">👩‍🏫</span>
                <span className="text-gray-700">Teacher Q&A</span>
              </Link>
              <Link to="/forum" className="flex items-center p-3 rounded-lg hover:bg-orange-50 transition-colors">
                <span className="mr-3">🧵</span>
                <span className="text-gray-700">Ask & Share (Ta'arof culture, jokes, common mistakes)</span>
              </Link>
            </div>
          </div>

          {/* Premium Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <FaStar className="mr-2 text-purple-600" />
              💎 Premium
            </h3>
            <div className="space-y-3">
              <Link to="/premium-benefits" className="flex items-center p-3 rounded-lg hover:bg-purple-50 transition-colors">
                <span className="mr-3">⭐</span>
                <span className="text-gray-700">Benefits</span>
              </Link>
              <Link to="/pricing" className="flex items-center p-3 rounded-lg hover:bg-purple-50 transition-colors">
                <span className="mr-3">💳</span>
                <span className="text-gray-700">Pricing</span>
              </Link>
              <Link to="/subscription" className="flex items-center p-3 rounded-lg hover:bg-purple-50 transition-colors">
                <span className="mr-3">🪄</span>
                <span className="text-gray-700">Unlock All</span>
              </Link>
            </div>
          </div>

          {/* My Account Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <FaUserTie className="mr-2 text-gray-600" />
              🔐 My Account
            </h3>
            <div className="space-y-3">
              {user ? (
                <>
                  <Link to="/profile" className="flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors">
                    <span className="mr-3">⚙️</span>
                    <span className="text-gray-700">Profile & Settings</span>
                  </Link>
                  <Link to="/dashboard" className="flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors">
                    <span className="mr-3">📊</span>
                    <span className="text-gray-700">Dashboard</span>
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/login" className="flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors">
                    <span className="mr-3">🔑</span>
                    <span className="text-gray-700">Log In</span>
                  </Link>
                  <Link to="/register" className="flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors">
                    <span className="mr-3">🔑</span>
                    <span className="text-gray-700">Sign Up</span>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Responsive mobile menu
  const handleResize = () => {
    // Handle resize if needed
  };
  React.useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Newsletter subscribe state
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterStatus, setNewsletterStatus] = useState<null | { type: 'success' | 'error', message: string }>(null);
  const [newsletterLoading, setNewsletterLoading] = useState(false);

  const handleNewsletterSubscribe = async () => {
    if (!newsletterEmail || !newsletterEmail.includes('@')) {
      setNewsletterStatus({ type: 'error', message: 'Please enter a valid email address.' });
      return;
    }
    setNewsletterLoading(true);
    setNewsletterStatus(null);
    try {
              const response = await fetch(`${API_BASE_URL}/api/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newsletterEmail, source: 'free-phrasebook' })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setNewsletterStatus({ type: 'success', message: data.message || 'Subscribed successfully!' });
        setNewsletterEmail("");
      } else {
        setNewsletterStatus({ type: 'error', message: data.message || 'Subscription failed.' });
      }
    } catch (err) {
      setNewsletterStatus({ type: 'error', message: 'Network error. Please try again.' });
    } finally {
      setNewsletterLoading(false);
    }
  };

  return (
    <>
      <SEO 
        title="پیروز (Pirooz) - Master Farsi Online | Learn Persian Language Fast"
        description="Learn Farsi (Persian) online with پیروز Pirooz - the most comprehensive platform for mastering Persian language. Interactive lessons, vocabulary builder, pronunciation guides, and cultural insights. Start your journey to fluency today!"
        keywords={[
          'learn farsi online', 'persian language course', 'farsi lessons', 'persian vocabulary',
          'farsi pronunciation', 'persian alphabet', 'farsi grammar', 'iranian culture',
          'persian language app', 'farsi flashcards', 'learn persian', 'farsi tutor',
          'persian language learning', 'farsi conversation', 'iran language', 'persian script'
        ]}
        canonicalUrl="/"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "EducationalOrganization",
          "name": "پیروز Pirooz",
          "description": "Comprehensive online Farsi (Persian) language learning platform",
          "url": "https://farsi-app.com",
          "logo": "https://farsi-app.com/logo512.png",
          "foundingDate": "2024",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "US"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "customer service",
            "email": "support@pingquack.com"
          },
          "sameAs": [
            "https://twitter.com/Pirooz_farsi",
            "https://facebook.com/Pirooz.farsi",
            "https://instagram.com/Pirooz_farsi"
          ],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Farsi Language Courses",
            "itemListElement": [
              {
                "@type": "Course",
                "name": "Beginner Farsi Course",
                "description": "Complete beginner course for learning Farsi from scratch",
                "provider": {
                  "@type": "EducationalOrganization",
                  "name": "پیروز Pirooz"
                }
              },
              {
                "@type": "Course", 
                "name": "Persian Alphabet Learning",
                "description": "Master the Persian alphabet and script",
                "provider": {
                  "@type": "EducationalOrganization",
                  "name": "پیروز Pirooz"
                }
              },
              {
                "@type": "Course",
                "name": "Farsi Vocabulary Builder", 
                "description": "Expand your Farsi vocabulary with interactive flashcards",
                "provider": {
                  "@type": "EducationalOrganization",
                  "name": "پیروز Pirooz"
                }
              }
            ]
          }
        }}
      />
      <div className="bg-white text-gray-800 min-h-screen">
      {/* Hero Section */}
      <div className="hero-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="md:flex md:items-center md:justify-between">
            <div className="md:w-1/2 mb-12 md:mb-0" dir="ltr">
              <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-4">
                Learn Farsi. <span className="text-blue-600">Speak from the Heart.</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8" dir="ltr">
                Discover the Persian language through storytelling, real conversation, and community.
                <span className="block mt-2 text-blue-600 persian-font" dir="rtl">(زبان فارسی را با قلب یاد بگیرید)</span>
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/alphabet-learning" className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition duration-300 shadow-lg pulse">
                  Learning Alphabet
                </Link>
                <Link to="/forum" className="px-6 py-3 bg-white text-blue-700 font-semibold rounded-lg hover:bg-gray-50 transition duration-300 border border-blue-600">
                  Explore Forum
                </Link>
              </div>
            </div>
            <div className="md:w-1/2 flex justify-center">
              <div className="relative w-full max-w-md">
                <div className="absolute -top-8 -left-8 w-32 h-32 bg-yellow-100 rounded-full opacity-70"></div>
                <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-indigo-100 rounded-full opacity-70"></div>
                <div className="relative bg-white p-8 rounded-2xl shadow-xl floating">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                      <FaHeadphones className="text-indigo-600" />
                    </div>
                    <h3 className="ml-3 font-medium">Lesson Preview</h3>
                  </div>
                  <h4 className="text-lg font-semibold mb-2">Greetings in Persian</h4>
                  <p className="text-gray-600 mb-4">Learn how to say hello and introduce yourself in Farsi.</p>
                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <p className="text-xl persian-font text-right mb-1" dir="rtl">سلام! چطوری؟</p>
                    <p className="text-gray-600" dir="ltr">Salam! Chetori? <span className="text-sm text-gray-400">(Hello! How are you?)</span></p>
                    <button className="mt-2 flex items-center text-indigo-600" dir="ltr" onClick={() => playAudio('/audio/salam-chetori.mp3')}>
                      <FaPlayCircle className="mr-2" /> Listen
                    </button>
                  </div>
                  <Link to="/pathways/kitchen-farsi" className="block text-center px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition duration-300">
                    Try Now – بدون ثبت‌نام
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* How It Works Section */}
      <div className="gradient-bg py-16 relative">
        <div className="wave-divider">
          <svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="shape-fill"></path>
          </svg>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" dir="ltr">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4 text-white">How Vajak Works</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our three-step approach makes learning Farsi natural, engaging, and effective.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition duration-300">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                <FaBookOpen className="text-indigo-600 text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-center">Learn</h3>
              <p className="text-gray-600 mb-4 text-center">
                Interactive lessons with native pronunciation, cultural insights, and engaging stories.
              </p>
              <div className="text-center">
                <span className="inline-block px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full text-sm">
                  مثال (Example)
                </span>
              </div>
            </div>
            {/* Step 2 */}
            <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition duration-300">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                <FaBrain className="text-yellow-600 text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-center">Practice</h3>
              <p className="text-gray-600 mb-4 text-center">
                Quizzes, flashcards, and AI tutors help reinforce what you've learned in real-world contexts.
              </p>
              <div className="text-center">
                <span className="inline-block px-3 py-1 bg-yellow-50 text-yellow-600 rounded-full text-sm">
                  تمرین (Practice)
                </span>
              </div>
            </div>
            {/* Step 3 */}
            <div className="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition duration-300">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                <FaUsers className="text-green-600 text-2xl" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-center">Connect</h3>
              <p className="text-gray-600 mb-4 text-center">
                Join forums, teacher-led discussions, and cultural chats to use your Farsi in real conversations.
              </p>
              <div className="text-center">
                <span className="inline-block px-3 py-1 bg-green-50 text-green-600 rounded-full text-sm">
                  ارتباط (Connection)
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Free Lesson Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:flex lg:flex-row lg:items-center lg:justify-between flex-col-reverse">
            <div className="lg:w-1/2 mb-12 lg:mb-0" dir="ltr">
              <h2 className="text-3xl font-bold mb-4" dir="ltr">Beginner's Course: Greetings & Introductions</h2>
              <p className="text-xl text-gray-600 mb-6" dir="ltr">
                Learn essential phrases for everyday conversations in Farsi.
              </p>
              <div className="bg-gray-50 p-6 rounded-lg mb-6" dir="ltr">
                <div className="flex items-center mb-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                    <FaVolumeUp className="text-indigo-600" />
                  </div>
                  <h3 className="ml-3 font-medium" dir="ltr">Today's Phrase</h3>
                </div>
                {loadingWord ? (
                  <div className="animate-pulse">
                    <div className="h-8 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded mb-3"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  </div>
                ) : (
                  <>
                    <p className="text-2xl persian-font text-left mb-1" dir="ltr">{wordOfTheDay.persian}</p>
                    <p className="text-gray-600" dir="ltr">{wordOfTheDay.transliteration} <span className="text-sm text-gray-400">({wordOfTheDay.english})</span></p>
                    <button className="mt-3 flex items-center text-indigo-600" dir="ltr" onClick={() => playAudio(wordOfTheDay.audioUrl)}>
                      <FaPlayCircle className="mr-2" /> Listen to pronunciation
                    </button>
                  </>
                )}
              </div>
              <Link to="/pathways" className="inline-block px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition duration-300 shadow-lg" dir="ltr">
                Start Free Lesson
              </Link>
            </div>
            <div className="lg:w-1/2 flex justify-center mb-12 lg:mb-0">
              <div className="bg-gray-100 rounded-xl overflow-hidden w-full max-w-md" dir="ltr">
                <div className="aspect-w-16 aspect-h-9">
                  <img src="https://images.unsplash.com/photo-1546410531-bb4caa6b424d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1471&q=80" 
                       alt="Learning Farsi" className="w-full h-full object-cover" />
                </div>
                <div className="p-6" dir="ltr">
                  <h3 className="text-xl font-bold mb-2" dir="ltr">Beginner's Course: Greetings & Introductions</h3>
                  <p className="text-gray-600 mb-4" dir="ltr">Learn essential phrases for everyday conversations in Farsi.</p>
                  <div className="flex items-center text-sm text-gray-500" dir="ltr">
                    <div className="flex items-center mr-4">
                      <FaPlayCircle className="mr-1 text-indigo-600" />
                      <span>12 video lessons</span>
                    </div>
                    <div className="flex items-center">
                      <FaHeadphones className="mr-1 text-indigo-600" />
                      <span>Audio exercises</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Premium Plans Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" dir="ltr">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Unlock More with Pirooz Plus</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Upgrade for advanced courses, offline access, and personalized learning.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Free Plan */}
            <div className="bg-white p-8 rounded-xl shadow-md border border-gray-200">
              <h3 className="text-xl font-bold mb-2">Free</h3>
              <p className="text-gray-600 mb-6">Great for getting started</p>
              <div className="mb-6">
                <span className="text-3xl font-bold">$0</span>
                <span className="text-gray-500">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center">
                  <FaCheck className="text-green-500 mr-2" />
                  <span className="text-gray-800 font-medium">Basic courses</span>
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-green-500 mr-2" />
                  <span className="text-gray-800 font-medium">Community forum access</span>
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-green-500 mr-2" />
                  <span className="text-gray-800 font-medium">Limited practice exercises</span>
                </li>
              </ul>
              <span className="block text-center px-4 py-2 border border-blue-600 text-blue-700 font-semibold rounded-lg bg-blue-50 transition duration-300">
                Current Plan
              </span>
            </div>
            {/* Premium Plan */}
            <div className="bg-white p-8 rounded-xl shadow-md border border-gray-200">
              <h3 className="text-xl font-bold mb-2">Premium</h3>
              <p className="text-gray-600 mb-6">For advanced learners</p>
              <div className="mb-6">
                <span className="text-3xl font-bold">$9.99</span>
                <span className="text-gray-500">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center">
                  <FaCheck className="text-green-500 mr-2" />
                  <span className="text-gray-800 font-medium">All Vajak Plus features</span>
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-green-500 mr-2" />
                  <span className="text-gray-800 font-medium">Advanced & specialized courses</span>
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-green-500 mr-2" />
                  <span className="text-gray-800 font-medium">Two 30-minute tutoring sessions/month</span>
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-green-500 mr-2" />
                  <span className="text-gray-800 font-medium">Personalized learning plan</span>
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-green-500 mr-2" />
                  <span className="text-gray-800 font-medium">Priority support</span>
                </li>
              </ul>
              <Link to="/subscription" className="block text-center px-4 py-2 border border-blue-600 text-blue-700 font-semibold rounded-lg hover:bg-blue-50 transition duration-300">
                Choose Premium
              </Link>
            </div>
            {/* Platinum Plan */}
            <div className="bg-gradient-to-br from-amber-300 to-indigo-200 p-8 rounded-xl shadow-xl border-2 border-yellow-400">
              <h3 className="text-xl font-bold mb-2 text-yellow-700">Platinum</h3>
              <p className="text-gray-700 mb-6 font-semibold">Ultimate experience for serious learners</p>
              <div className="mb-6">
                <span className="text-3xl font-bold text-yellow-700">$24.99</span>
                <span className="text-gray-700">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center">
                  <FaCheck className="text-green-600 mr-2" />
                  <span className="text-gray-900 font-medium">Everything in Premium</span>
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-green-600 mr-2" />
                  <span className="text-gray-900 font-medium">Unlimited tutoring sessions</span>
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-green-600 mr-2" />
                  <span className="text-gray-900 font-medium">1-on-1 coaching with native speakers</span>
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-green-600 mr-2" />
                  <span className="text-gray-900 font-medium">Exclusive cultural events & webinars</span>
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-green-600 mr-2" />
                  <span className="text-gray-900 font-medium">Certificate of completion</span>
                </li>
                <li className="flex items-center">
                  <FaCheck className="text-green-600 mr-2" />
                  <span className="text-gray-900 font-medium">Direct support from Vajak team</span>
                </li>
              </ul>
              <Link to="/subscription" className="block text-center px-4 py-2 border border-yellow-600 text-yellow-800 font-bold rounded-lg bg-yellow-100 hover:bg-yellow-200 transition duration-300 shadow-lg">
                Go Platinum
              </Link>
            </div>
          </div>
          <div className="text-center mt-8">
            <Link to="/pricing" className="text-blue-700 hover:text-blue-800 font-semibold">
              Compare all plans <FaChevronRight className="ml-1 inline" />
            </Link>
          </div>
        </div>
      </div>
      {/* For Teachers Section */}
      <div className="bg-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" dir="ltr">
          <div className="lg:flex lg:items-center lg:justify-between">
            <div className="lg:w-1/2 mb-12 lg:mb-0">
              <h2 className="text-3xl font-bold mb-4 text-white">Teach with Pirooz</h2>
              <p className="text-xl text-white mb-6">
                Share your knowledge of Persian language and culture while earning money on your own schedule.
              </p>
              <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
                <div className="flex items-center mb-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                    <FaChalkboardTeacher className="text-indigo-600" />
                  </div>
                  <h3 className="ml-3 font-medium">Why Teach with Pirooz?</h3>
                </div>
                <ul className="space-y-2" dir="ltr">
                  <li className="flex items-start">
                    <FaCheck className="text-green-500 mt-1 mr-2" />
                    <span className="text-gray-800 font-medium" dir="ltr">Reach thousands of eager learners worldwide</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheck className="text-green-500 mt-1 mr-2" />
                    <span className="text-gray-800 font-medium" dir="ltr">Set your own prices and teaching schedule</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheck className="text-green-500 mt-1 mr-2" />
                    <span className="text-gray-800 font-medium" dir="ltr">Use our easy course creation tools</span>
                  </li>
                  <li className="flex items-start">
                    <FaCheck className="text-green-500 mt-1 mr-2" />
                    <span className="text-gray-800 font-medium" dir="ltr">Get paid monthly for your courses</span>
                  </li>
                </ul>
              </div>
              <Link to="/become-teacher" className="inline-block px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition duration-300 shadow-lg">
                Become a Pirooz Teacher
              </Link>
            </div>
            <div className="lg:w-1/2">
              <div className="bg-white p-8 rounded-xl shadow-lg" dir="ltr">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center">
                    <FaUserTie className="text-indigo-600 text-2xl" />
                  </div>
                  <div className="ml-4">
                    <h3 className="font-bold">Teacher Spotlight</h3>
                    <p className="text-gray-600">Meet Nasrin, Persian teacher from Tehran</p>
                  </div>
                </div>
                <blockquote className="text-gray-700 italic mb-6">
                  "Teaching on Vajak has allowed me to share my love for Persian language and culture with students from all over the world. The platform makes it easy to create engaging lessons and connect with learners."
                </blockquote>
                <div className="flex items-center">
                  <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=688&q=80" 
                       alt="Teacher" className="w-12 h-12 rounded-full object-cover" />
                  <div className="ml-4">
                    <h4 className="font-medium">Nasrin Nickparsa</h4>
                    <p className="text-sm text-gray-500">Persian teacher since 2015</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Forum Snapshot Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" dir="ltr">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Join the Conversation</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ask questions, share your experiences, and connect with other learners in our forum. Want to learn more about Persian sayings? <Link to="/idioms" className="text-orange-600 underline hover:text-orange-800 font-semibold">Explore Persian Idioms & Proverbs</Link>.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {/* Forum Topic 1 */}
            <Link to="/idioms" className="bg-gray-50 p-6 rounded-xl hover:shadow-md transition duration-300 block focus:outline-none focus:ring-2 focus:ring-orange-400">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                  <FaLightbulb className="text-yellow-600" />
                </div>
                <h3 className="ml-3 font-medium">Persian Idioms</h3>
              </div>
              <p className="text-gray-600 mb-4">
                What does "گربه را دم حجله کشتن" (killing the cat at the wedding canopy) mean? Let's discuss Persian idioms and their origins.
              </p>
              <div className="flex items-center text-sm text-gray-500">
                <div className="flex items-center mr-4">
                  <FaComment className="mr-1 text-indigo-600" />
                  <span>24 replies</span>
                </div>
                <div className="flex items-center">
                  <FaEye className="mr-1 text-indigo-600" />
                  <span>156 views</span>
                </div>
              </div>
            </Link>
            {/* Forum Topic 2 */}
            <Link to="/tarof-tips" className="bg-gray-50 p-6 rounded-xl hover:shadow-md transition duration-300 block focus:outline-none focus:ring-2 focus:ring-blue-400">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <FaHandsHelping className="text-green-600" />
                </div>
                <h3 className="ml-3 font-medium">Ta'arof Tips</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Navigating Persian politeness can be tricky! Share your experiences with ta'arof and how you've learned to respond appropriately.
              </p>
              <div className="flex items-center text-sm text-gray-500">
                <div className="flex items-center mr-4">
                  <FaComment className="mr-1 text-indigo-600" />
                  <span>42 replies</span>
                </div>
                <div className="flex items-center">
                  <FaEye className="mr-1 text-indigo-600" />
                  <span>289 views</span>
                </div>
              </div>
            </Link>
            {/* Forum Topic 3 */}
            <Link to="/forum" className="bg-gray-50 p-6 rounded-xl hover:shadow-md transition duration-300 block focus:outline-none focus:ring-2 focus:ring-blue-400">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  <FaQuestion className="text-indigo-600" />
                </div>
                <h3 className="ml-3 font-medium">What's the word for...?</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Can't find the right word in Persian? Ask here! Recently: "How do you say 'serendipity' in Farsi?"
              </p>
              <div className="flex items-center text-sm text-gray-500">
                <div className="flex items-center mr-4">
                  <FaComment className="mr-1 text-indigo-600" />
                  <span>18 replies</span>
                </div>
                <div className="flex items-center">
                  <FaEye className="mr-1 text-indigo-600" />
                  <span>97 views</span>
                </div>
              </div>
            </Link>
          </div>
          <div className="text-center mt-12">
            <Link to="/forum" className="inline-block px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition duration-300 shadow-lg">
              Visit Forum
            </Link>
          </div>
        </div>
      </div>
      {/* Testimonials Section */}
      <div className="bg-gray-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" dir="ltr">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">What Our Community Says</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from learners and teachers who are using Vajak to connect through Persian.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300" dir="ltr">
                <div className="flex items-center mb-4">
                  <img src={testimonial.img} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                  <div className="ml-3 min-w-0 flex-1">
                    <h4 className="font-medium text-gray-900 text-sm">{testimonial.name}</h4>
                    <p className="text-xs text-gray-500">{testimonial.role}</p>
                  </div>
                </div>
                <div className="flex mb-3">
                  {[...Array(5)].map((_, i) => (
                    <FaStar key={i} className="text-yellow-400 w-3 h-3" />
                  ))}
                </div>
                <p className="text-gray-700 text-sm leading-relaxed">{testimonial.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Free Phrasebook Section */}
      <div className="bg-indigo-600 py-16 text-white !bg-indigo-600" style={{ backgroundColor: '#4f46e5' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:flex lg:items-center lg:justify-between">
            <div className="lg:w-1/2 mb-12 lg:mb-0" dir="ltr">
              <h2 className="text-3xl font-bold mb-4 text-white">Get Your Free Persian Phrasebook</h2>
              <p className="text-xl text-white mb-6">
                Sign up for our newsletter and receive a downloadable PDF with essential Persian phrases, pronunciation guide, and cultural tips.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 max-w-md">
                <input
                  type="email"
                  placeholder="Your email address"
                  className="px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 text-gray-800"
                  value={newsletterEmail}
                  onChange={e => setNewsletterEmail(e.target.value)}
                  disabled={newsletterLoading}
                />
                <button
                  className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition duration-300 shadow-lg whitespace-nowrap disabled:opacity-60"
                  onClick={handleNewsletterSubscribe}
                  disabled={newsletterLoading}
                >
                  {newsletterLoading ? 'Subscribing...' : 'Get My Free Phrasebook'}
                </button>
              </div>
              {newsletterStatus && (
                <p className={`mt-3 text-sm ${newsletterStatus.type === 'success' ? 'text-green-200' : 'text-red-200'}`}>{newsletterStatus.message}</p>
              )}
              <p className="text-sm text-indigo-200 mt-3">
                We respect your privacy. Unsubscribe at any time.
              </p>
            </div>
            <div className="lg:w-1/2 flex justify-center">
              <div className="relative w-full max-w-md">
                <div className="absolute -top-8 -left-8 w-32 h-32 bg-indigo-500 rounded-full opacity-20"></div>
                <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-indigo-400 rounded-full opacity-20"></div>
                <div className="relative bg-white p-6 rounded-xl shadow-xl">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                      <FaBook className="text-indigo-600" />
                    </div>
                    <h3 className="ml-3 font-medium text-gray-800">Sample from Phrasebook</h3>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <p className="text-xl persian-font text-right mb-1" dir="rtl">لطفاً</p>
                      <p className="text-gray-600" dir="ltr">Lotfan <span className="text-sm text-gray-400">(Please)</span></p>
                    </div>
                    <div>
                      <p className="text-xl persian-font text-right mb-1" dir="rtl">متشکرم</p>
                      <p className="text-gray-600" dir="ltr">Moteshakkeram <span className="text-sm text-gray-400">(Thank you)</span></p>
                    </div>
                    <div>
                      <p className="text-xl persian-font text-right mb-1" dir="rtl">چطور می‌توانم به ... برسم؟</p>
                      <p className="text-gray-600" dir="ltr">Chetor mitavanam be ... beresam? <span className="text-sm text-gray-400">(How can I get to...?)</span></p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Footer */}
      <footer className="bg-gray-900 text-white pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8">
            <div className="col-span-2">
              <div className="flex items-center mb-4">
                <span className="text-2xl font-bold text-indigo-400 persian-font">پیروز</span>
                <span className="ml-2 text-xl font-bold text-white">Pirooz</span>
              </div>
              <p className="text-gray-400 mb-4">
                Learn Farsi through storytelling, real conversation, and community.
              </p>
              <div className="flex space-x-4">
                <a href="https://instagram.com/piroozapp" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                  <FaInstagram className="text-xl" />
                </a>
                <a href="https://youtube.com/piroozapp" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                  <FaYoutube className="text-xl" />
                </a>
                <a href="https://t.me/piroozapp" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                  <FaTelegram className="text-xl" />
                </a>
                <a href="https://twitter.com/piroozapp" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                  <FaTwitter className="text-xl" />
                </a>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">Learn</h3>
              <ul className="space-y-2">
                <li><Link to="/pathways" className="text-gray-400 hover:text-white">Courses</Link></li>
                <li><Link to="/pricing" className="text-gray-400 hover:text-white">Pricing</Link></li>
                <li><Link to="/features" className="text-gray-400 hover:text-white">Features</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-white">About</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">Community</h3>
              <ul className="space-y-2">
                <li><Link to="/forum" className="text-gray-400 hover:text-white">Forum</Link></li>
                <li><Link to="/events" className="text-gray-400 hover:text-white">Events</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white">Blog</Link></li>
                <li><Link to="/success-stories" className="text-gray-400 hover:text-white">Success Stories</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">Company</h3>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-gray-400 hover:text-white">About</Link></li>
                <li><Link to="/become-teacher" className="text-gray-400 hover:text-white">Teach with Us</Link></li>
                <li><Link to="/careers" className="text-gray-400 hover:text-white">Careers</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition">Contact</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center">
            <div className="flex space-x-6 mb-4 md:mb-0">
              <Link to="/privacy" className="text-gray-400 hover:text-white text-sm">Privacy Policy</Link>
              <Link to="/terms" className="text-gray-400 hover:text-white text-sm">Terms of Service</Link>
              <Link to="/cookies" className="text-gray-400 hover:text-white text-sm">Cookie Policy</Link>
            </div>
            <div className="flex items-center">
              <div className="flex items-center mr-4">
                <FaGlobe className="text-gray-400 mr-2" />
                <select className="bg-gray-800 text-white text-sm focus:outline-none">
                  <option>English</option>
                  <option>فارسی</option>
                </select>
              </div>
              <p className="text-gray-400 text-sm">
                &copy; 2024 پیروز Pirooz. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <Sidebar />
    </div>
    </>
  );
};

export default Home;

